import {
    ExtendedConstructionNumber,
    ExtendedProjectPhase,
    ExtendedPropertyType,
} from '@bpd-library/utilities';
import { Filters } from '@features/properties/components/properties-filters/filters';
import createStore from '@utilities/store';

import { transformFilters } from './utilities/transform-filters';
import { transformPayload } from './utilities/transform-payload';

// Saved within the store
export interface FilterInput {
    value: string;
    name: string;
    checked: boolean;
    id: string;
    label: string;
    type: string;
}

export interface PropertiesFilterFunctions {
    constructionNumber: (
        constructionNumber: ExtendedConstructionNumber,
        filterInput: FilterInput,
    ) => boolean;
    propertyType: (propertyType: ExtendedPropertyType, filterInput: FilterInput) => boolean;
    projectPhase: (projectPhase: ExtendedProjectPhase, filterInput: FilterInput) => boolean;
}

export interface SelectedFilter {
    hasRangeValues: boolean;
    filterFunctions: PropertiesFilterFunctions;
    filterLogic?: FilterLogic;
    inputs: FilterInput[];
}

export interface FilterState {
    [key: string]: SelectedFilter;
}

export type FilterLogic = '&&' | '||';

export interface Filter {
    name: string;
    id: string;
    checked: boolean;
    value: string;
    type: string;
    hasRangeValues: boolean;
    filterLogic?: FilterLogic;
}

interface State {
    filters: FilterState;
    isLoading: boolean;
    currentFilterChange: string;
    isOpen: boolean;
}

const initialState: State = {
    filters: {},
    isLoading: true,
    currentFilterChange: '',
    isOpen: false,
};

const ADD_FILTERS = 'addFilters';
const UPDATE_FILTERS = 'updateFilters';
const OPEN_FILTERS = 'openFilters';
const RESET_FILTERS = 'resetFiltes';

const actions = {
    [ADD_FILTERS]: (state: State, payload: Filters) => {
        state.filters = transformPayload(payload);
        state.isLoading = false;
        return state;
    },
    [UPDATE_FILTERS]: (state: State, payload: Filter) => {
        const currentFilter = state.filters[payload.name];
        const { filterLogic } = currentFilter;

        state.currentFilterChange = payload.name;

        state.filters = {
            ...state.filters,
            [payload.name]: {
                ...currentFilter,
                inputs: currentFilter.inputs.map((input) => {
                    // if filters has range values and is a radio set all checked radio falseexcept the current id
                    if (payload.type === 'radio' && payload.hasRangeValues) {
                        input.checked = payload.id === input.id ? payload.checked : false;
                    }

                    // If type is checkbox or radio and not a range value update only the checked status when the id of the input is the same as the input of the payload
                    if (
                        (payload.type === 'checkbox' || payload.type === 'radio') &&
                        !payload.hasRangeValues &&
                        input.id === payload.id
                    ) {
                        input.checked = payload.checked;
                    }

                    if (filterLogic === '&&' && payload.id === input.id) {
                        currentFilter.inputs.map((input) => (input.checked = payload.checked));
                        input.value = payload.value;
                    }

                    return input;
                }),
            },
        };

        return state;
    },

    [OPEN_FILTERS]: (state: State, payload: boolean) => {
        state.isOpen = payload;
        return state;
    },
    [RESET_FILTERS]: (state: State, payload: boolean) => {
        if (payload) {
            const newFilters: FilterState = {};

            Object.keys(state.filters).forEach((filterName) => {
                const filter = state.filters[filterName];

                newFilters[filterName] = {
                    ...filter,
                    inputs: filter.inputs.map((input) => ({ ...input, checked: false })),
                };
            });
            state.currentFilterChange = 'all';
            state.filters = { ...newFilters };
        }

        return state;
    },
};

export const filterStore = createStore<State>({
    initialState,
    actions,
});

export const addFilters = (payload: Filters) => {
    filterStore.dispatch(ADD_FILTERS, payload);
};

export const getSelectedFilters = () => {
    const { filters } = filterStore.getState();

    return transformFilters(filters);
};

export const getSelectedFiltersByType = (type: string) => {
    const { filters } = filterStore.getState();
    const selectedFilters = transformFilters(filters);
    return selectedFilters.find((filter) => filter.filterName === type);
};

export const getAllFilters = () => {
    return filterStore.getState().filters;
};

export const getFilterInputByValue = (name: string, value: string) => {
    const { filters } = filterStore.getState();

    const filter = filters[name];

    return filter.inputs.find((input) => input.value === value) as FilterInput;
};

export const updateFilter = (payload: Filter) => {
    filterStore.dispatch(UPDATE_FILTERS, payload);
};

export const getFilterByType = (type: string) => {
    const { filters } = filterStore.getState();
    return filters[type];
};

export const toggleFilters = (state: boolean) => {
    filterStore.dispatch(OPEN_FILTERS, state);
};

export const resetFilters = (state: boolean) => filterStore.dispatch(RESET_FILTERS, state);
